import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Combobox, DateTime, Autocomplete } from '~/components/Form';
import { t } from '~/helpers';
import { closeDialog } from '~/store/modules/dialog/actions';
import {
  storeOperacaoAfericao,
  recordFiltering,
} from '~/store/modules/operacaoafericao/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
}) {
  const dispatch = useDispatch();

  const [posto, setPosto] = useState(0);
  const [inicio, setInicio] = useState(0);
  const [fim, setFim] = useState(0);
  const [volumeDiferenca, setVolumeDiferenca] = useState(0);
  const [volumeObservado, setVolumeObservado] = useState(0);
  const [volumeRegistrado, setVolumeRegistrado] = useState(0);

  const { client, id } = useSelector((state) => state.user.profile);

  const { loading, params } = useSelector((state) => state.operacaoafericao);

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        idbico: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        datahorainicialafericao: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        encerranteinicialafericao: Yup.number().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        encerrantefinalafericao: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(
            Number(param.encerranteinicialafericao).toFixed(2),
            t('s.geral.validacao.valorminimo').replace(
              '%s',
              Number(param.encerranteinicialafericao).toFixed(2)
            )
          ),
        volumeregistradoafericao: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(
            (fim - inicio).toFixed(2),
            t('s.geral.validacao.valormaximo').replace(
              '%s',
              (fim - inicio).toFixed(2)
            )
          )
          .min(
            (fim - inicio).toFixed(2),
            t('s.geral.validacao.valorminimo').replace(
              '%s',
              (fim - inicio).toFixed(2)
            )
          ),
        volumeaferidoafericao: Yup.number().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        observacaoafericao: Yup.string().nullable(),
      });

      await schema.validate(
        { ...param, volumeregistradoafericao: volumeRegistrado },
        {
          abortEarly: false,
        }
      );

      const { datahorainicialafericao } = param;

      const objSend = {
        ...param,
        idcliente: client,
        idusuario: id,
        datahorainicialafericao,
        datahorafinalafericao: datahorainicialafericao,
        vazaomaximaafericao: 0.0,
        volumeregistradoafericao: volumeRegistrado,
        volumeaferidoafericao: Number(param.volumeaferidoafericao),
      };

      await dispatch(storeOperacaoAfericao(objSend, params));
      await dispatch(recordFiltering(params));
      await dispatch(closeDialog());
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    setVolumeRegistrado(fim - inicio);
  }, [inicio, fim]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} onSubmit={handleSubmit}>
          <Autocomplete
            name="idposto"
            disabled={!create}
            disableClearable={!create}
            label="codigonomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.menu.configuracao.posto')}
            getValue={(value) => {
              if (value.idposto) {
                setPosto(value.idposto);
              }
            }}
          />

          <Combobox
            name="idbico"
            disabled={!create || posto === 0}
            label="bicoprodutobico"
            url="bicos"
            unique="idbico"
            title={t('s.menu.configuracao.bico')}
            params={{
              wherefield: !create
                ? ['idposto', 'tipoproduto']
                : [
                    'idposto',
                    'tipoproduto',
                    'bloqueiadigitacaomovimentacaobico',
                  ],
              valuewherefield: !create ? [posto, 'C'] : [posto, 'C', 'S'],
              typewherefield: !create
                ? ['EQUALS', 'EQUALS']
                : ['EQUALS', 'EQUALS', 'EQUALS'],
              fieldlist: 'idbico, idterminal, idproduto, bicoprodutobico',
            }}
          />

          <DateTime
            time
            name="datahorainicialafericao"
            disabled={!create}
            label={t('s.operacao.abastecimento.datahorainicialmovimentacao')}
          />

          <Input
            name="encerranteinicialafericao"
            disabled={!create}
            label={t('s.operacao.abastecimento.encerranteinicialmovimentacao')}
            type="number"
            getValue={(value) => setInicio(value)}
          />

          <Input
            name="encerrantefinalafericao"
            disabled={!create}
            label={t('s.operacao.abastecimento.encerrantefinalmovimentacao')}
            type="number"
            getValue={(value) => setFim(value)}
          />

          <Input
            name="volumeregistradoafericao"
            disabled
            label={t('s.geral.camposistema.volumeregistradoafericao')}
            type="number"
            value={fim - inicio}
            getValue={(value) => setVolumeRegistrado(value)}
          />
          <Input
            name="volumeaferidoafericao"
            disabled={!create}
            label={t('s.geral.camposistema.volumeaferidoafericao')}
            type="number"
            getValue={(value) => setVolumeObservado(value)}
          />

          <Input
            name="vazaomaximaafericao"
            disabled
            label={t('s.geral.camposistema.volumediferenca')}
            type="number"
            value={volumeRegistrado - volumeObservado}
            getValue={(value) => setVolumeDiferenca(value)}
          />

          <Input
            name="observacaoafericao"
            label={t('s.geral.camposistema.observacaoafericao')}
            multiline
            rows={5}
            inputProps={{ maxLength: 200 }}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func.isRequired,
};

CreateOrUpdate.defaultProps = {
  data: {},
};
